import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Chip,
  LinearProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../store/authStore';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, requestPasswordReset, resetPassword, error: authError, clearError } = useAuthStore();
  const [loading, setLoading] = useState(false);
  
  // Состояния для восстановления пароля
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  
  // Состояния для отображения паролей
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Состояния для проверки сложности пароля
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasOnlyLatinAndSpecial: true
  });
  const [passwordScore, setPasswordScore] = useState(0);
  
  const from = location.state?.from || '/';

  // Форма для входа
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  });
  
  // Форма для восстановления пароля (шаг 1: email)
  const { 
    register: registerReset, 
    handleSubmit: handleSubmitReset, 
    formState: { errors: errorsReset } 
  } = useForm({
    defaultValues: {
      resetEmail: ''
    }
  });
  
  // Форма для восстановления пароля (шаг 2: временный пароль и новый пароль)
  const { 
    register: registerNewPassword, 
    handleSubmit: handleSubmitNewPassword, 
    formState: { errors: errorsNewPassword },
    watch
  } = useForm({
    defaultValues: {
      tempPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });
  
  // Для проверки совпадения паролей
  const newPassword = watch('newPassword');
  const watchConfirmPassword = watch('confirmPassword');
  
  // Проверка сложности пароля
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength({
        hasMinLength: false,
        hasUpperCase: false,
        hasNumber: false,
        hasSpecialChar: false,
        hasOnlyLatinAndSpecial: true
      });
      setPasswordScore(0);
      return;
    }
    
    const strength = {
      hasMinLength: newPassword.length >= 6,
      hasUpperCase: /[A-Z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(newPassword),
      hasOnlyLatinAndSpecial: /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/.test(newPassword)
    };
    
    setPasswordStrength(strength);
    
    // Расчет оценки (0-5)
    const score = Object.values(strength).filter(Boolean).length;
    setPasswordScore(score);
  }, [newPassword]);
  
  // Получение метки и цвета для сложности пароля
  const getPasswordStrengthInfo = () => {
    if (!newPassword) return { label: '', color: '' };
    
    if (passwordScore === 5) return { label: 'Отличный пароль', color: 'success.main' };
    if (passwordScore === 4) return { label: 'Хороший пароль', color: 'success.light' };
    if (passwordScore === 3) return { label: 'Средний пароль', color: 'warning.main' };
    return { label: 'Слабый пароль', color: 'error.main' };
  };
  
  const passwordInfo = getPasswordStrengthInfo();

  // Обработка входа
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      clearError();
      const success = await login(data.email, data.password);
      if (success) {
        navigate(from, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Открыть диалог восстановления пароля
  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
    setResetStep(0);
    setResetSuccess(false);
    setResetMessage('');
    clearError();
  };
  
  // Закрыть диалог восстановления пароля
  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
  };
  
  // Переключение видимости временного пароля
  const handleToggleTempPasswordVisibility = () => {
    setShowTempPassword(!showTempPassword);
  };
  
  // Переключение видимости нового пароля
  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  
  // Переключение видимости подтверждения пароля
  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  // Шаг 1: Запрос на восстановление пароля
  const handleRequestReset = async (data) => {
    try {
      setLoading(true);
      clearError();
      const email = data.resetEmail;
      setResetEmail(email);
      
      const result = await requestPasswordReset(email);
      if (result.success) {
        setResetStep(1);
        setResetSuccess(true);
        setResetMessage(result.message || 'На ваш email отправлен временный пароль');
      } else {
        setResetSuccess(false);
        setResetMessage(result.message || 'Не удалось отправить запрос на восстановление пароля');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Шаг 2: Сброс пароля с временным паролем
  const handleResetPassword = async (data) => {
    try {
      setLoading(true);
      clearError();
      
      const success = await resetPassword(resetEmail, data.tempPassword, data.newPassword);
      if (success) {
        setResetStep(2);
        setResetSuccess(true);
        setResetMessage('Пароль успешно изменен! Вы вошли в систему.');
        setTimeout(() => {
          setResetDialogOpen(false);
          navigate(from, { replace: true });
        }, 2000);
      } else {
        setResetSuccess(false);
        setResetMessage('Не удалось сбросить пароль. Проверьте временный пароль и попробуйте снова.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 8
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Вход в систему
          </Typography>
          
          {authError && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {authError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              autoComplete="email"
              autoFocus
              {...register('email', {
                required: 'Email обязателен',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Неверный формат email'
                }
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              label="Пароль"
              type="password"
              autoComplete="current-password"
              {...register('password', {
                required: 'Пароль обязателен',
                minLength: {
                  value: 6,
                  message: 'Пароль должен содержать минимум 6 символов'
                }
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Войти'}
            </Button>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, mb: 2 }}>
              <Link component={RouterLink} to="/register" variant="body2">
                Нет аккаунта? Зарегистрироваться
              </Link>
              <Link component="button" variant="body2" onClick={handleOpenResetDialog}>
                Забыли пароль?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
      
      {/* Диалог восстановления пароля */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <LockResetIcon sx={{ mr: 1, color: 'primary.main' }} />
          Восстановление пароля
        </DialogTitle>
        
        <Divider />
        
        <DialogContent>
          {/* Шаги восстановления пароля */}
          <Stepper activeStep={resetStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Запрос</StepLabel>
            </Step>
            <Step>
              <StepLabel>Сброс пароля</StepLabel>
            </Step>
            <Step>
              <StepLabel>Готово</StepLabel>
            </Step>
          </Stepper>
          
          {/* Сообщение об успехе или ошибке */}
          {resetMessage && (
            <Alert severity={resetSuccess ? "success" : "error"} sx={{ mb: 2 }}>
              {resetMessage}
            </Alert>
          )}
          
          {/* Шаг 1: Запрос на восстановление пароля */}
          {resetStep === 0 && (
            <Box component="form" onSubmit={handleSubmitReset(handleRequestReset)}>
              <DialogContentText sx={{ mb: 2 }}>
                Введите email, указанный при регистрации. На него будет отправлен временный пароль для восстановления доступа.
              </DialogContentText>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="resetEmail"
                label="Email"
                autoComplete="email"
                autoFocus
                {...registerReset('resetEmail', {
                  required: 'Email обязателен',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Неверный формат email'
                  }
                })}
                error={!!errorsReset.resetEmail}
                helperText={errorsReset.resetEmail?.message}
              />
              
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button onClick={handleCloseResetDialog}>Отмена</Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Отправить'}
                </Button>
              </DialogActions>
            </Box>
          )}
          
          {/* Шаг 2: Ввод временного пароля и создание нового */}
          {resetStep === 1 && (
            <Box component="form" onSubmit={handleSubmitNewPassword(handleResetPassword)}>
              <DialogContentText sx={{ mb: 2 }}>
                Введите временный пароль, который был отправлен на ваш email, и создайте новый пароль.
              </DialogContentText>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="tempPassword"
                label="Временный пароль"
                type={showTempPassword ? 'text' : 'password'}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleToggleTempPasswordVisibility}
                        edge="end"
                      >
                        {showTempPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...registerNewPassword('tempPassword', {
                  required: 'Временный пароль обязателен'
                })}
                error={!!errorsNewPassword.tempPassword}
                helperText={errorsNewPassword.tempPassword?.message}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="newPassword"
                label="Новый пароль"
                type={showNewPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleToggleNewPasswordVisibility}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...registerNewPassword('newPassword', {
                  required: 'Новый пароль обязателен',
                  minLength: {
                    value: 6,
                    message: 'Пароль должен содержать минимум 6 символов'
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/,
                    message: 'Пароль должен содержать только латинские буквы, цифры и специальные символы'
                  },
                  validate: {
                    hasUpperCase: value => /[A-Z]/.test(value) || 'Пароль должен содержать хотя бы одну заглавную букву',
                    hasNumber: value => /[0-9]/.test(value) || 'Пароль должен содержать хотя бы одну цифру',
                    hasSpecialChar: value => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(value) || 'Пароль должен содержать хотя бы один специальный символ (!@#$%^&*()_+ и т.д.)'
                  }
                })}
                error={!!errorsNewPassword.newPassword}
                helperText={errorsNewPassword.newPassword?.message}
              />
              
              {newPassword && (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: passwordInfo.color, display: 'block', mb: 0.5 }}>
                    {passwordInfo.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Chip 
                      size="small" 
                      label="Мин. 6 символов" 
                      color={passwordStrength.hasMinLength ? "success" : "default"}
                      variant={passwordStrength.hasMinLength ? "filled" : "outlined"}
                    />
                    <Chip 
                      size="small" 
                      label="Заглавная буква" 
                      color={passwordStrength.hasUpperCase ? "success" : "default"}
                      variant={passwordStrength.hasUpperCase ? "filled" : "outlined"}
                    />
                    <Chip 
                      size="small" 
                      label="Цифра" 
                      color={passwordStrength.hasNumber ? "success" : "default"}
                      variant={passwordStrength.hasNumber ? "filled" : "outlined"}
                    />
                    <Chip 
                      size="small" 
                      label="Специальный символ" 
                      color={passwordStrength.hasSpecialChar ? "success" : "default"}
                      variant={passwordStrength.hasSpecialChar ? "filled" : "outlined"}
                    />
                    <Chip 
                      size="small" 
                      label="Только латиница и спецсимволы" 
                      color={passwordStrength.hasOnlyLatinAndSpecial ? "success" : "error"}
                      variant={passwordStrength.hasOnlyLatinAndSpecial ? "filled" : "outlined"}
                    />
                  </Box>
                </Box>
              )}
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="confirmPassword"
                label="Подтверждение пароля"
                type={showConfirmPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleToggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...registerNewPassword('confirmPassword', {
                  required: 'Подтверждение пароля обязательно',
                  validate: value => value === newPassword || 'Пароли не совпадают'
                })}
                error={!!errorsNewPassword.confirmPassword}
                helperText={errorsNewPassword.confirmPassword?.message}
              />
              
              {/* Индикатор совпадения паролей */}
              {newPassword && watchConfirmPassword && (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: newPassword === watchConfirmPassword ? 'success.main' : 'error.main',
                      display: 'block' 
                    }}
                  >
                    {newPassword === watchConfirmPassword 
                      ? 'Пароли совпадают' 
                      : 'Пароли не совпадают'}
                  </Typography>
                </Box>
              )}
              
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button onClick={handleCloseResetDialog}>Отмена</Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Сбросить пароль'}
                </Button>
              </DialogActions>
            </Box>
          )}
          
          {/* Шаг 3: Успешное восстановление */}
          {resetStep === 2 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Пароль успешно изменен!
              </Typography>
              <Typography variant="body1">
                Вы будете перенаправлены на главную страницу...
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default LoginPage;
